import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PdfAdBanner } from '../components/AdSystem';
import { 
  ArrowLeft, Printer, Clock, Wrench, Package, AlertTriangle, Shield,
  ListOrdered, Lightbulb, FileText, Calendar, CheckCircle2, BookOpen
} from 'lucide-react';
import LeonAvatar from '../components/LeonAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Conseils de Léon pour les recettes travaux
const leonTips = [
  "Avant de commencer, vérifiez toujours l'état réel du support. Une bonne préparation est souvent la clé d'un résultat propre et durable.",
  "Prenez le temps de bien lire toutes les étapes avant de vous lancer. Anticiper les difficultés, c'est déjà les éviter.",
  "N'hésitez pas à faire un test sur une petite surface avant de traiter l'ensemble. Mieux vaut prévenir que guérir !",
  "Les bons outils font les bons ouvriers. Investir dans du matériel de qualité vous fera gagner du temps et du résultat.",
  "Si vous avez un doute sur une étape technique, faites appel à un professionnel. Certains travaux méritent un regard expert."
];

// Composant En-tête de page (pour impression)
const PageHeader = ({ title, icon: Icon }) => (
  <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-amber-500 print-header">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-amber-500" />
      </div>
      <h2 className="text-xl font-bold text-zinc-900">{title}</h2>
    </div>
    <div className="flex items-center gap-2">
      <img 
        src="/assets/phenix-logo.png" 
        alt="Assistant by Léon" 
        className="h-8 w-auto" 
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      <span className="text-sm font-semibold text-zinc-600">Assistant by Léon</span>
    </div>
  </div>
);

// Composant Pied de page (pour impression)
const PageFooter = ({ pageNumber, totalPages }) => (
  <div className="mt-auto pt-6 border-t border-zinc-200 print-footer">
    <div className="flex items-center justify-between text-xs text-zinc-500">
      <div className="flex items-center gap-4">
        <span className="font-medium">Assistant by Léon</span>
        <span>Un produit de l'écosystème Phénix</span>
      </div>
      <span>Document généré automatiquement par Assistant by Léon</span>
      <span className="font-medium">Page {pageNumber}{totalPages ? ` / ${totalPages}` : ''}</span>
    </div>
  </div>
);

export default function PrintableRecipe() {
  const { recipeId } = useParams();
  const { getAuthHeaders } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [randomTip] = useState(() => leonTips[Math.floor(Math.random() * leonTips.length)]);

  useEffect(() => {
    fetchRecipe();
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Recette non trouvée');
      }
      
      const data = await response.json();
      setRecipe(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getDifficultyStyle = (level) => {
    switch (level?.toLowerCase()) {
      case 'facile': return { color: '#10b981', bg: '#d1fae5', label: 'Facile' };
      case 'moyen': return { color: '#f59e0b', bg: '#fef3c7', label: 'Moyen' };
      case 'difficile': return { color: '#f97316', bg: '#ffedd5', label: 'Difficile' };
      case 'expert': return { color: '#ef4444', bg: '#fee2e2', label: 'Expert' };
      default: return { color: '#71717a', bg: '#f4f4f5', label: level || 'Non défini' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Chargement de la recette...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Recette non trouvée</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link to="/recipes" className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-black rounded-lg hover:bg-amber-400 font-bold">
            <ArrowLeft className="w-5 h-5" />
            Retour aux recettes
          </Link>
        </div>
      </div>
    );
  }

  const recipeData = recipe?.recipe || {};
  const difficulty = getDifficultyStyle(recipeData.difficulty_level);

  return (
    <>
      {/* Boutons d'action (cachés à l'impression) */}
      <div className="print:hidden fixed top-0 left-0 right-0 bg-zinc-900 border-b border-zinc-800 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/recipes" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Retour
          </Link>
          <button 
            onClick={() => window.print()} 
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Imprimer / Enregistrer PDF
          </button>
        </div>
      </div>

      {/* Contenu du rapport - Design professionnel pour impression */}
      <div className="min-h-screen bg-white pt-20 print:pt-0 print:bg-white">
        
        {/* ==================== PAGE 1 : COUVERTURE ==================== */}
        <div className="print-page bg-white min-h-screen flex flex-col p-8">
          {/* En-tête avec logo */}
          <div className="flex items-center justify-between pb-6 border-b-2 border-amber-500">
            <div className="flex items-center gap-4">
              <img 
                src="/assets/phenix-logo.png" 
                alt="Assistant by Léon" 
                className="h-16 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-2xl font-bold text-zinc-900">
                  <span className="text-amber-600">Assistant by Léon</span>
                </h1>
                <p className="text-sm text-zinc-500">L'intelligence artificielle qui sécurise vos travaux</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-zinc-500">Document généré le</p>
              <p className="font-semibold text-zinc-700">{formatDate(recipe?.created_at)}</p>
            </div>
          </div>

          {/* Titre du document */}
          <div className="flex-1 flex flex-col justify-center items-center text-center py-12">
            <div className="mb-8">
              <div className="w-20 h-1 bg-amber-500 mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-zinc-900 mb-2 tracking-wide">
                RECETTE TRAVAUX
              </h2>
              <p className="text-lg text-zinc-500">
                Méthode détaillée générée par Assistant by Léon
              </p>
              <div className="w-20 h-1 bg-amber-500 mx-auto mt-6" />
            </div>

            {/* Carte du projet */}
            <div className="bg-zinc-50 border-2 border-zinc-200 rounded-2xl p-8 max-w-xl w-full shadow-sm">
              <h3 className="text-2xl font-bold text-amber-600 mb-6">
                {recipeData.title || 'Projet de travaux'}
              </h3>
              
              <p className="text-zinc-600 leading-relaxed mb-6">
                {recipe?.project_description}
              </p>
              
              <div className="grid grid-cols-2 gap-6 pt-6 border-t border-zinc-200">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: difficulty.bg }}>
                    <AlertTriangle className="w-6 h-6" style={{ color: difficulty.color }} />
                  </div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Difficulté</p>
                  <p className="font-bold text-lg" style={{ color: difficulty.color }}>{difficulty.label}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-2 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Durée estimée</p>
                  <p className="font-bold text-lg text-zinc-800">{recipeData.estimated_duration || 'Non définie'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pied de page */}
          <PageFooter pageNumber={1} />
        </div>

        {/* ==================== PAGE 2 : MATÉRIAUX ET OUTILS ==================== */}
        <div className="print-page bg-white min-h-screen flex flex-col p-8">
          <PageHeader title="Synthèse du Projet" icon={BookOpen} />

          {/* Matériaux */}
          {recipeData.materials && recipeData.materials.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-500" />
                Liste des matériaux nécessaires
              </h3>
              <div className="bg-zinc-50 rounded-xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-zinc-700 font-semibold border-b border-zinc-200">Matériau</th>
                      <th className="text-left px-4 py-3 text-zinc-700 font-semibold border-b border-zinc-200">Quantité</th>
                      <th className="text-right px-4 py-3 text-zinc-700 font-semibold border-b border-zinc-200">Prix estimé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipeData.materials.map((material, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                        <td className="px-4 py-3 text-zinc-800 font-medium">{material.name}</td>
                        <td className="px-4 py-3 text-zinc-600">{material.quantity}</td>
                        <td className="px-4 py-3 text-amber-600 text-right font-semibold">{material.price_estimate || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Outils */}
          {recipeData.tools && recipeData.tools.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-500" />
                Outils nécessaires
              </h3>
              <div className="flex flex-wrap gap-2">
                {recipeData.tools.map((tool, i) => (
                  <span 
                    key={i} 
                    className={`px-4 py-2 rounded-lg text-sm border ${
                      tool.essential 
                        ? 'bg-amber-50 text-amber-800 border-amber-300 font-semibold' 
                        : 'bg-zinc-50 text-zinc-700 border-zinc-200'
                    }`}
                  >
                    {tool.name} {tool.essential && '★'}
                  </span>
                ))}
              </div>
              <p className="text-xs text-zinc-500 mt-3">★ Outil essentiel pour ce projet</p>
            </div>
          )}

          <PageFooter pageNumber={2} />
        </div>

        {/* ==================== PAGE 3 : ÉTAPES DÉTAILLÉES ==================== */}
        {recipeData.steps && recipeData.steps.length > 0 && (
          <div className="print-page bg-white min-h-screen flex flex-col p-8">
            <PageHeader title="Étapes Détaillées" icon={ListOrdered} />

            <div className="space-y-5 flex-1">
              {recipeData.steps.map((step, i) => (
                <div key={i} className="bg-zinc-50 rounded-xl p-5 border border-zinc-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{step.step_number || i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-zinc-900 mb-2">{step.title}</h4>
                      <p className="text-zinc-600 text-sm leading-relaxed mb-2">{step.description}</p>
                      
                      {step.duration && (
                        <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
                          <Clock className="w-3 h-3" />
                          <span>{step.duration}</span>
                        </div>
                      )}
                      
                      {step.tips && step.tips.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                          <p className="text-amber-700 text-xs font-semibold mb-1 flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" />
                            Conseils
                          </p>
                          <ul className="text-zinc-700 text-xs space-y-0.5">
                            {step.tips.map((tip, j) => (
                              <li key={j}>• {tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <PageFooter pageNumber={3} />
          </div>
        )}

        {/* ==================== PAGE 4 : VIGILANCE ET SÉCURITÉ ==================== */}
        <div className="print-page bg-white min-h-screen flex flex-col p-8">
          <PageHeader title="Points de Vigilance" icon={Shield} />

          {/* Erreurs à éviter */}
          {recipeData.common_mistakes && recipeData.common_mistakes.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Erreurs courantes à éviter
              </h3>
              <div className="space-y-3">
                {recipeData.common_mistakes.map((mistake, i) => {
                  // Handle both string format and object format
                  const isString = typeof mistake === 'string';
                  const mistakeText = isString ? mistake : (mistake.mistake || mistake.text || mistake.label || '');
                  const howToAvoid = isString ? null : mistake.how_to_avoid;
                  
                  return (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-700 font-semibold text-sm">{mistakeText}</p>
                      {howToAvoid && (
                        <p className="text-zinc-600 text-xs flex items-start gap-2 mt-1">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          {howToAvoid}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Conseils de sécurité */}
          {recipeData.safety_tips && recipeData.safety_tips.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                Consignes de sécurité
              </h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <ul className="space-y-2">
                  {recipeData.safety_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Le conseil de Léon */}
          <div className="bg-zinc-900 rounded-xl p-5 mt-auto mb-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <LeonAvatar className="w-14 h-14" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <h3 className="text-base font-bold text-amber-500">Le conseil de Léon</h3>
                </div>
                <p className="text-zinc-300 text-sm italic leading-relaxed">
                  "{randomTip}"
                </p>
              </div>
            </div>
          </div>

          <PageFooter pageNumber={4} />
        </div>

        {/* ==================== PAGE 5 : MENTIONS LÉGALES ==================== */}
        <div className="print-page bg-white min-h-screen flex flex-col p-8">
          <PageHeader title="Mentions Importantes" icon={FileText} />

          {/* Avertissement */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-zinc-900 mb-2">Avertissement</h3>
                <p className="text-zinc-700 text-sm leading-relaxed">
                  Cette recette travaux est fournie à titre informatif et indicatif. Elle ne constitue ni un diagnostic 
                  technique exhaustif, ni une validation de conformité, ni un substitut à l'intervention d'un professionnel qualifié.
                </p>
              </div>
            </div>
          </div>

          {/* Recommandations */}
          <div className="bg-zinc-50 rounded-xl p-6 border border-zinc-200 mb-6">
            <h3 className="text-base font-bold text-zinc-900 mb-4">Nos recommandations</h3>
            <div className="space-y-2">
              {[
                "Vérifiez toujours l'état réel du support avant de commencer les travaux",
                "Respectez les consignes de sécurité et portez les équipements de protection appropriés",
                "En cas de doute technique, faites appel à un professionnel qualifié",
                "Vérifiez que vos travaux sont conformes aux réglementations en vigueur",
                "Conservez les factures et garanties des matériaux utilisés"
              ].map((rec, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-700 text-sm">{rec}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Clause de non-responsabilité */}
          <div className="bg-zinc-100 rounded-xl p-4 border border-zinc-200 text-xs text-zinc-600 leading-relaxed mb-auto">
            <strong>Clause de non-responsabilité :</strong> Assistant by Léon ne pourra être tenu responsable de tout dommage, 
            malfaçon, erreur de mise en œuvre, surcoût, désordre ou litige survenant avant, pendant ou après les travaux. 
            L'utilisateur est seul responsable de la mise en œuvre des conseils fournis.
          </div>

          {/* Pied de page final avec branding */}
          <div className="border-t-2 border-amber-500 pt-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/assets/phenix-logo.png" 
                  alt="Assistant by Léon" 
                  className="h-12 w-auto" 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <p className="font-bold text-zinc-900">Assistant by Léon</p>
                  <p className="text-xs text-zinc-500">Un produit de l'écosystème Phénix</p>
                </div>
              </div>
              <div className="text-right text-xs text-zinc-500">
                <p className="font-medium">Document généré automatiquement</p>
                <p>par Assistant by Léon le {formatDate(recipe?.created_at)}</p>
              </div>
            </div>
          </div>

          {/* === PUBLICITÉ (Utilisateurs gratuits uniquement) === */}
          <PdfAdBanner />
        </div>
      </div>

      {/* Styles d'impression optimisés */}
      <style>{`
        @media print {
          body { 
            background: white !important; 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:pt-0 { padding-top: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print-page { 
            page-break-after: always;
            break-after: page;
            min-height: 100vh;
          }
          .print-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .print-header, .print-footer {
            position: relative;
          }
          @page { 
            margin: 1cm; 
            size: A4;
          }
          /* Force background colors */
          .bg-zinc-50, .bg-zinc-100, .bg-amber-50, .bg-green-50, .bg-red-50 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-zinc-900 {
            background-color: #18181b !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-amber-500 {
            background-color: #f59e0b !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        @media screen {
          .print-page {
            margin-bottom: 2rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            border-radius: 0.5rem;
          }
        }
      `}</style>
    </>
  );
}
